import { WaypointType } from '@treadinc/horizon-api-spec';

import { AddressOption } from '~components/Order/SiteSelection/types';
import { Order } from '~hooks/useOrders/models';
import { Geofence, Site } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

export interface ReplaySite {
  geofence: Geofence | null;
  id: string;
  markerType: WaypointType | 'both';
  name: string;
  position: { lat: number; lng: number };
}

interface PlaceableSite {
  geofence: Geofence | null;
  id: string;
  lat: Nullable<number | string>;
  lng: Nullable<number | string>;
  name: string;
}

const fromWaypointSite = (site: Site): PlaceableSite => ({
  geofence: site.nextBillionGeofence ?? null,
  id: site.id,
  lat: site.lat,
  lng: site.lon,
  name: site.name,
});

const fromDefaultSite = (option: AddressOption): PlaceableSite => ({
  geofence: option.geofence ?? null,
  id: option.id,
  lat: option.lat,
  lng: option.lng,
  name: option.name,
});

export function buildReplaySites(
  order: Order,
  defaultSites: { drop_off: AddressOption[]; pickup: AddressOption[] },
) {
  const sitesById = new Map<string, ReplaySite>();

  const addSite = (site: PlaceableSite, markerType: WaypointType) => {
    const lat = parseFiniteNumber(site.lat);
    const lng = parseFiniteNumber(site.lng);
    if (lat === null || lng === null) return;

    const existing = sitesById.get(site.id);
    if (existing) {
      if (existing.markerType !== markerType) existing.markerType = 'both';
      if (!existing.geofence) existing.geofence = site.geofence;

      return;
    }

    sitesById.set(site.id, {
      geofence: site.geofence,
      id: site.id,
      markerType,
      name: site.name,
      position: { lat, lng },
    });
  };

  (order.waypoints ?? []).forEach((waypoint) => {
    if (waypoint.siteNested) {
      addSite(fromWaypointSite(waypoint.siteNested), waypoint.type);
    }
  });
  defaultSites.pickup.forEach((option) =>
    addSite(fromDefaultSite(option), WaypointType.PICKUP),
  );
  defaultSites.drop_off.forEach((option) =>
    addSite(fromDefaultSite(option), WaypointType.DROP_OFF),
  );

  return [...sitesById.values()];
}
